import type { KeyboardEvent } from 'react';

const STRATEGY_CELL_SELECTOR = '[data-strategy-field]';
const FOCUSABLE_CONTROL_SELECTOR = 'button, input, select, textarea, [href], [tabindex]';

/** Overrides Tab to navigate field first, then leg. */
export function handleFieldTab(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key !== 'Tab' || event.ctrlKey || event.altKey || event.metaKey) {
    return;
  }

  const currentControl = event.target;
  const strategyGrid = event.currentTarget.closest<HTMLElement>('.strategy-grid');
  if (!(currentControl instanceof HTMLElement) || strategyGrid === null) {
    return;
  }

  const nextControl = getNextControl(strategyGrid, currentControl, event.shiftKey);
  if (nextControl === undefined) {
    return;
  }

  event.preventDefault();
  currentControl.blur();
  focusControlAfterRender(strategyGrid, nextControl);
}

/** Returns the adjacent control in field-first order. */
export function getNextControl(
  strategyGrid: HTMLElement,
  currentControl: HTMLElement,
  backwards: boolean,
): HTMLElement | undefined {
  // The DOM is leg-first. Group cells by field to produce:
  // field 1 leg 1, field 1 leg 2, field 2 leg 1, field 2 leg 2.
  const cells = Array.from(strategyGrid.querySelectorAll<HTMLElement>(STRATEGY_CELL_SELECTOR)).filter(
    (cell) => !cell.classList.contains('d-none'),
  );
  const fieldOrder = [...new Set(cells.map((cell) => cell.dataset.strategyField))];
  const orderedControls = fieldOrder.flatMap((field) =>
    cells.filter((cell) => cell.dataset.strategyField === field).flatMap(getCellControls),
  );
  const currentIndex = orderedControls.indexOf(currentControl);
  const direction = backwards ? -1 : 1;

  return orderedControls[currentIndex + direction];
}

/** Focuses the target after blur-triggered state updates have rerendered it. */
function focusControlAfterRender(strategyGrid: HTMLElement, nextControl: HTMLElement) {
  const nextCell = nextControl.closest<HTMLElement>(STRATEGY_CELL_SELECTOR);
  if (nextCell === null) {
    return;
  }

  const nextField = nextCell.dataset.strategyField;
  const nextLeg = nextCell.dataset.strategyLeg;
  const nextControlIndex = getCellControls(nextCell).indexOf(nextControl);

  requestAnimationFrame(() => {
    const refreshedCell = Array.from(strategyGrid.querySelectorAll<HTMLElement>(STRATEGY_CELL_SELECTOR)).find(
      (cell) => cell.dataset.strategyField === nextField && cell.dataset.strategyLeg === nextLeg,
    );
    getCellControls(refreshedCell ?? nextCell)[nextControlIndex]?.focus();
  });
}

/** Returns a cell form controls, or its first focusable element when it has none. */
function getCellControls(cell: HTMLElement): HTMLElement[] {
  const focusables = getFocusableElements(cell);
  const formControls = focusables.filter((element) => element.matches('input, select, textarea'));

  return formControls.length > 0 ? formControls : focusables.slice(0, 1);
}

/** Returns the enabled, editable elements that can receive keyboard focus. */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute('disabled') && !element.hasAttribute('readonly') && element.tabIndex >= 0,
  );
}